import { useState, type ReactNode } from "react";
import { ChevronDownIcon, XIcon } from "lucide-react";

import { PopoverTrigger } from "@/components/ui/popover";
import { cn } from "cn";

interface ChipPopoverTriggerProps {
  active?: boolean;
  children: ReactNode;
  clearable?: boolean;
  disabled?: boolean;
  onClear?: () => void;
}

function ChipPopoverTrigger({
  active,
  children,
  clearable,
  disabled,
  onClear,
}: ChipPopoverTriggerProps) {
  const [clearing, setClearing] = useState(false);
  const showClear = active && clearable && !disabled;

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    setClearing(true);

    window.setTimeout(() => {
      onClear?.();
      setClearing(false);
    }, 600);
  };

  return (
    <PopoverTrigger
      disabled={disabled}
      render={
        <button
          type="button"
          // Shape/sizing unchanged; colors match Button outline + aria-expanded
          // (popup open). Filter-active uses the same muted surface.
          className={cn(
            "relative mx-1 inline-flex h-8 cursor-pointer items-center justify-center rounded-full border border-border bg-background text-sm leading-none font-medium shadow-xs outline-none",
            "hover:bg-muted hover:text-foreground",
            "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
            "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
            "aria-expanded:bg-muted aria-expanded:text-foreground",
            active && "bg-muted text-foreground"
          )}
        />
      }
    >
      <span
        className={cn(
          "flex h-full items-center rounded-full pr-1 pl-3",
          active && "pr-9 hover:bg-muted"
        )}
      >
        {children}
      </span>
      {showClear ? (
        <XIcon
          className={cn(
            "absolute right-0 box-content size-4 shrink-0 rounded-full px-2 py-1.75",
            "bg-center hover:[background:white_radial-gradient(circle,transparent_1%,white_1%)_center/0%]",
            clearing &&
            "bg-muted bg-size-[15000%] transition-[background] duration-600 ease-in"
          )}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={handleClear}
        />
      ) : (
        <ChevronDownIcon
          className={cn(
            "box-content size-4 shrink-0 rounded-full px-2 py-1.75",
            active && "absolute right-0"
          )}
        />
      )}
    </PopoverTrigger>
  );
}

export default ChipPopoverTrigger;
